import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, FileUp, Inbox, CreditCard, Sparkles, CheckCheck, Headset } from "lucide-react";
import { getAlerts, markAlertsRead } from "@/lib/portalApi";

const ICONS = { message: MessageSquare, document: FileUp, application: Inbox, payment: CreditCard, ai_draft: Sparkles, support: Headset };

const ago = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return new Date(iso).toLocaleDateString();
};

/** Bell in the ERP header: customer messages, uploads, new applications, payments, AI drafts. */
export const StaffAlerts = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const baseTitle = useRef(document.title.replace(/^\(\d+\)\s*/, ""));

  const load = useCallback(async () => {
    try { setData(await getAlerts(30)); } catch (e) { /* offline or logged out */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    document.title = data.unread > 0 ? `(${data.unread}) ${baseTitle.current}` : baseTitle.current;
  }, [data.unread]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openItem = async (it) => {
    setOpen(false);
    if (!it.read) { await markAlertsRead({ ids: [it.id] }).catch(() => {}); load(); }
    if (it.link) navigate(it.link);
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white"
        aria-label={`Alerts${data.unread ? `, ${data.unread} unread` : ""}`}
        data-testid="staff-alerts-bell"
      >
        <Bell className="h-5 w-5" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-testid="staff-alerts-count">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[90vw] overflow-hidden rounded-xl border border-white/10 bg-venka-ink shadow-2xl" data-testid="staff-alerts-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
            <span className="text-sm font-semibold">Alerts</span>
            {data.unread > 0 && (
              <button type="button" className="flex items-center gap-1 text-xs text-venka-green-light hover:underline"
                onClick={async () => { await markAlertsRead({}); load(); }}>
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-[26rem] overflow-y-auto divide-y divide-white/5">
            {data.items.length === 0 && <li className="px-4 py-8 text-center text-sm text-slate-400">Nothing new.</li>}
            {data.items.map((it) => {
              const Icon = ICONS[it.kind] || Bell;
              return (
                <li key={it.id}>
                  <button type="button" onClick={() => openItem(it)}
                    className={`flex w-full gap-3 px-4 py-3 text-left hover:bg-white/5 ${it.read ? "opacity-60" : ""}`}>
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${it.read ? "text-slate-400" : "text-venka-green-light"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-100">{it.title}</span>
                      {it.body && <span className="mt-0.5 block truncate text-xs text-slate-400">{it.body}</span>}
                    </span>
                    <span className="shrink-0 text-[11px] text-slate-500">{ago(it.created_at)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StaffAlerts;
